import {
  DisplayType,
  FieldTypeEnum,
  FormattingType,
  KeyValueFormattingModel,
  KeyValueRestrictionModel,
  RestrictionType,
} from '@models/module/fields/enums';

import {
  FIELD_TYPE_LABELS,
  FIELD_TYPE_PRESENTATIONS,
  fieldDisplayPresentationOf,
} from '@shared/components/field-type-indicator/field-type-presentation';

export { FIELD_TYPE_LABELS, fieldIcon } from '@shared/components/field-type-indicator/field-type-presentation';

export type RestrictionName =
  | 'ReadOnly'
  | 'Required'
  | 'NeedsToBeChecked'
  | 'Number'
  | 'DateTime'
  | 'MimeTypes'
  | 'ShowThousandSeparator'
  | 'MaximumLength';

export type FormattingName = 'Text' | 'Number' | 'Decimals';

export interface CatalogElement {
  key: number;
  label: string;
}

export interface RestrictionDefinition {
  key: RestrictionType;
  label: string;
  elements: CatalogElement[];
  defaultValue?: boolean | number;
  required?: boolean;
  isMimeTypes?: boolean;
  isNumberInput?: boolean;
}

export interface FormattingDefinition {
  key: FormattingType;
  label: string;
  elements: CatalogElement[];
  defaultValue?: number;
  defaultFormattingValue?: number;
}

export interface DisplayTypeDefinition {
  displayTypeId: DisplayType;
  label: string;
  labelMultiple: string;
  icon: string;
  formattings: FormattingName[];
  defaultFormattingIds: number[];
  hasDataSource: boolean;
  canHaveMultipleValues?: boolean;
}

export type DefaultRestriction = boolean | number | number[];

export interface FieldTypeDefinition {
  typeId: FieldTypeEnum;
  label: string;
  icon: string;
  canHaveMultipleValues: boolean;
  restrictions: RestrictionName[];
  defaultRestrictions: DefaultRestriction[];
  displayTypes: DisplayTypeDefinition[];
}

export interface GridDefaults {
  cols: number;
  rows: number;
  minItemCols?: number;
  minItemRows?: number;
  maxItemRows?: number;
  maxItemCols?: number;
}

export const ALL_MIME_TYPES_KEY = 0;

export const RESTRICTIONS: Record<RestrictionName, RestrictionDefinition> = {
  ReadOnly: {
    key: RestrictionType.ReadOnly,
    label: 'modules.fields.restrictions.readOnly',
    elements: [],
    defaultValue: false,
  },
  Required: {
    key: RestrictionType.Required,
    label: 'modules.fields.restrictions.required',
    elements: [],
    defaultValue: false,
  },
  NeedsToBeChecked: {
    key: RestrictionType.NeedsToBeChecked,
    label: 'modules.fields.restrictions.needsToBeChecked',
    elements: [],
    defaultValue: false,
  },
  Number: {
    key: RestrictionType.Number,
    label: 'modules.fields.restrictions.number',
    elements: [
      { key: 1, label: 'modules.fields.restrictions.decimal' },
      { key: 2, label: 'modules.fields.restrictions.integer' },
    ],
    required: true,
    defaultValue: 0,
  },
  DateTime: {
    key: RestrictionType.DateTime,
    label: 'modules.fields.restrictions.dateTime',
    elements: [
      { key: 3, label: 'modules.fields.restrictions.date' },
      { key: 4, label: 'modules.fields.restrictions.dateAndTime' },
      { key: 5, label: 'modules.fields.restrictions.time' },
    ],
    required: true,
    defaultValue: 0,
  },
  MimeTypes: {
    key: RestrictionType.MimeTypes,
    label: 'modules.fields.restrictions.mimeTypes',
    elements: [],
    isMimeTypes: true,
  },
  ShowThousandSeparator: {
    key: RestrictionType.ShowThousandSeparator,
    label: 'modules.fields.restrictions.showThousandSeparator',
    elements: [],
    defaultValue: true,
  },
  MaximumLength: {
    key: RestrictionType.MaximumLength,
    label: 'modules.fields.restrictions.maximumLength',
    elements: [],
    isNumberInput: true,
  },
};

export const FORMATTINGS: Record<FormattingName, FormattingDefinition> = {
  Text: {
    key: FormattingType.Text,
    label: 'modules.fields.formattings.text',
    elements: [
      { key: 6, label: 'modules.fields.formattings.plainText' },
      { key: 5, label: 'modules.fields.formattings.multiline' },
      { key: 4, label: 'modules.fields.formattings.editor' },
    ],
    defaultValue: 1,
  },
  Number: {
    key: FormattingType.Number,
    label: 'modules.fields.formattings.number',
    elements: [
      { key: 1, label: 'modules.fields.formattings.plainNumber' },
      { key: 2, label: 'modules.fields.formattings.money' },
      { key: 3, label: 'modules.fields.formattings.percentage' },
    ],
    defaultValue: 1,
  },
  Decimals: {
    key: FormattingType.Decimals,
    label: 'modules.fields.formattings.decimals',
    elements: [],
    defaultFormattingValue: 2,
  },
};

const display = (
  typeId: FieldTypeEnum,
  displayTypeId: DisplayType,
  options: Partial<
    Pick<DisplayTypeDefinition, 'formattings' | 'defaultFormattingIds' | 'hasDataSource' | 'canHaveMultipleValues'>
  > = {},
): DisplayTypeDefinition => ({
  ...fieldDisplayPresentationOf(typeId, displayTypeId),
  formattings: options.formattings ?? [],
  defaultFormattingIds: options.defaultFormattingIds ?? [],
  hasDataSource: options.hasDataSource ?? false,
  canHaveMultipleValues: options.canHaveMultipleValues ?? true,
});

export const FIELD_TYPES: Partial<Record<FieldTypeEnum, FieldTypeDefinition>> = {
  [FieldTypeEnum.Text]: {
    typeId: FieldTypeEnum.Text,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Text],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Text].icon,
    canHaveMultipleValues: true,
    restrictions: ['ReadOnly', 'Required', 'MaximumLength'],
    defaultRestrictions: [false, false],
    displayTypes: [
      display(FieldTypeEnum.Text, DisplayType.Text),
      display(FieldTypeEnum.Text, DisplayType.MultiLine, { canHaveMultipleValues: false }),
      display(FieldTypeEnum.Text, DisplayType.RichText, { canHaveMultipleValues: false }),
      display(FieldTypeEnum.Text, DisplayType.Select, { hasDataSource: true }),
      display(FieldTypeEnum.Text, DisplayType.Radio, { hasDataSource: true }),
    ],
  },
  [FieldTypeEnum.Number]: {
    typeId: FieldTypeEnum.Number,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Number],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Number].icon,
    canHaveMultipleValues: true,
    restrictions: ['ReadOnly', 'Required', 'ShowThousandSeparator', 'Number'],
    defaultRestrictions: [false, false, false, 1],
    displayTypes: [
      display(FieldTypeEnum.Number, DisplayType.Text, {
        formattings: ['Number', 'Decimals'],
        defaultFormattingIds: [1, 2],
      }),
    ],
  },
  [FieldTypeEnum.YesNo]: {
    typeId: FieldTypeEnum.YesNo,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.YesNo],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.YesNo].icon,
    canHaveMultipleValues: false,
    restrictions: ['ReadOnly', 'NeedsToBeChecked'],
    defaultRestrictions: [false, false],
    displayTypes: [display(FieldTypeEnum.YesNo, DisplayType.CheckBox)],
  },
  [FieldTypeEnum.Date]: {
    typeId: FieldTypeEnum.Date,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Date],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Date].icon,
    canHaveMultipleValues: false,
    restrictions: ['ReadOnly', 'Required', 'DateTime'],
    defaultRestrictions: [false, false, 3],
    displayTypes: [display(FieldTypeEnum.Date, DisplayType.Text)],
  },
  [FieldTypeEnum.Attachment]: {
    typeId: FieldTypeEnum.Attachment,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Attachment],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Attachment].icon,
    canHaveMultipleValues: true,
    restrictions: ['ReadOnly', 'Required', 'MimeTypes'],
    defaultRestrictions: [false, false, [ALL_MIME_TYPES_KEY]],
    displayTypes: [display(FieldTypeEnum.Attachment, DisplayType.Attachment)],
  },
  [FieldTypeEnum.Table]: {
    typeId: FieldTypeEnum.Table,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Table],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Table].icon,
    canHaveMultipleValues: false,
    restrictions: ['Required'],
    defaultRestrictions: [false],
    displayTypes: [display(FieldTypeEnum.Table, DisplayType.Table)],
  },
  [FieldTypeEnum.Relation]: {
    typeId: FieldTypeEnum.Relation,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Relation],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Relation].icon,
    canHaveMultipleValues: true,
    restrictions: ['ReadOnly', 'Required'],
    defaultRestrictions: [false, false],
    displayTypes: [display(FieldTypeEnum.Relation, DisplayType.Select, { hasDataSource: true })],
  },
  [FieldTypeEnum.Weather]: {
    typeId: FieldTypeEnum.Weather,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Weather],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Weather].icon,
    canHaveMultipleValues: false,
    restrictions: ['Required'],
    defaultRestrictions: [false],
    displayTypes: [display(FieldTypeEnum.Weather, DisplayType.Weather)],
  },
  [FieldTypeEnum.Address]: {
    typeId: FieldTypeEnum.Address,
    label: FIELD_TYPE_LABELS[FieldTypeEnum.Address],
    icon: FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Address].icon,
    canHaveMultipleValues: true,
    restrictions: ['ReadOnly', 'Required'],
    defaultRestrictions: [false, false],
    displayTypes: [
      display(FieldTypeEnum.Address, DisplayType.Address),
      display(FieldTypeEnum.Address, DisplayType.AddressLocationName),
      display(FieldTypeEnum.Address, DisplayType.AddressOnly),
      display(FieldTypeEnum.Address, DisplayType.AddressAutocomplete),
    ],
  },
};

export const ADDABLE_FIELD_TYPES: FieldTypeEnum[] = [
  FieldTypeEnum.Text,
  FieldTypeEnum.Number,
  FieldTypeEnum.YesNo,
  FieldTypeEnum.Date,
  FieldTypeEnum.Attachment,
  FieldTypeEnum.Table,
  FieldTypeEnum.Weather,
  FieldTypeEnum.Address,
];

const PAYMENT_MODULE_IDS = [-3, -4, -5];

export const isKnownFieldType = (typeId: number): boolean => !!FIELD_TYPE_LABELS[typeId as FieldTypeEnum];

export const displayTypeOf = (typeId: number, displayTypeId: number): DisplayTypeDefinition =>
  FIELD_TYPES[typeId as FieldTypeEnum]?.displayTypes.find(entry => entry.displayTypeId === displayTypeId);

export const addableFieldTypes = (moduleId: number, perContract: boolean): FieldTypeDefinition[] =>
  ADDABLE_FIELD_TYPES.filter(
    typeId => typeId !== FieldTypeEnum.Weather || (perContract && !PAYMENT_MODULE_IDS.includes(moduleId)),
  ).map(typeId => FIELD_TYPES[typeId]);

const GRID_TEXT: GridDefaults = { cols: 6, rows: 1, minItemCols: 2, maxItemRows: 1 };
const GRID_NUMBER: GridDefaults = { cols: 4, rows: 1, minItemCols: 2, maxItemRows: 1 };
const GRID_WIDE: GridDefaults = { cols: 12, rows: 1, minItemRows: 1, minItemCols: 3, maxItemRows: 1 };
const GRID_EDITOR: GridDefaults = { cols: 12, rows: 1, minItemRows: 1, minItemCols: 3, maxItemRows: 12 };
const GRID_RADIO: GridDefaults = { cols: 6, rows: 1, minItemCols: 3, maxItemRows: 3 };
const GRID_TABLE: GridDefaults = { cols: 12, rows: 1, minItemRows: 1, minItemCols: 6, maxItemRows: 12 };
const GRID_ATTACHMENT: GridDefaults = { cols: 12, rows: 1, maxItemRows: 1, minItemCols: 3, maxItemCols: 12 };
const GRID_FULL_ROW: GridDefaults = { cols: 12, rows: 1, maxItemRows: 1 };

type GridField = { typeId?: number; displayTypeId?: number };

const GRID_RULES: [(field: GridField) => boolean, GridDefaults][] = [
  [f => f.displayTypeId === DisplayType.Text && f.typeId === FieldTypeEnum.Text, GRID_TEXT],
  [
    f =>
      f.displayTypeId === DisplayType.Text && (f.typeId === FieldTypeEnum.Number || f.typeId === FieldTypeEnum.Formula),
    GRID_NUMBER,
  ],
  [f => f.displayTypeId === DisplayType.MultiLine, GRID_WIDE],
  [f => f.displayTypeId === DisplayType.RichText, GRID_EDITOR],
  [f => f.typeId === FieldTypeEnum.YesNo, GRID_TEXT],
  [
    f =>
      f.displayTypeId === DisplayType.Select || f.typeId === FieldTypeEnum.User || f.typeId === FieldTypeEnum.Relation,
    GRID_TEXT,
  ],
  [f => f.typeId === FieldTypeEnum.Date, GRID_TEXT],
  [f => f.typeId === FieldTypeEnum.Annotation, GRID_WIDE],
  [f => f.typeId === FieldTypeEnum.Attachment, GRID_ATTACHMENT],
  [f => f.typeId === FieldTypeEnum.Table, GRID_TABLE],
  [f => f.displayTypeId === DisplayType.Radio, GRID_RADIO],
  [f => f.typeId === FieldTypeEnum.Weather || f.displayTypeId === DisplayType.Weather, GRID_FULL_ROW],
  [f => f.typeId === FieldTypeEnum.Address || f.displayTypeId === DisplayType.Address, GRID_FULL_ROW],
];

export const gridDefaults = (field: GridField): GridDefaults =>
  GRID_RULES.filter(([matches]) => matches(field)).pop()?.[1];

export interface FieldBadges {
  typeLabel: string;
  displayLabel?: string;
  details: string[];
}

export const fieldBadges = (field: {
  typeId?: number;
  displayTypeId?: number;
  formattings?: KeyValueFormattingModel[];
  restrictions?: KeyValueRestrictionModel[];
}): FieldBadges => {
  const displayType = displayTypeOf(field.typeId, field.displayTypeId);
  const details: string[] = [];

  (field.formattings ?? []).forEach(formatting => {
    const definition = Object.values(FORMATTINGS).find(entry => entry.key === formatting.key);
    const element = definition?.elements.find(entry => entry.key === (formatting.value as number));
    if (formatting.value && element) {
      details.push(element.label);
    }
  });

  (field.restrictions ?? []).forEach(restriction => {
    const definition = Object.values(RESTRICTIONS).find(entry => entry.key === restriction.key);
    const element = definition?.elements.find(entry => entry.key === (restriction.value as number));
    if (restriction.value && element) {
      details.push(element.label);
    }
  });

  return {
    typeLabel: FIELD_TYPE_LABELS[field.typeId as FieldTypeEnum],
    displayLabel: displayType && field.displayTypeId !== DisplayType.Text ? displayType.label : undefined,
    details,
  };
};
